import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  Calendar,
  CalendarDays,
  BookOpen,
  Target,
  Users,
  Activity,
  Layers,
  PieChart,
  Wallet,
  LayoutDashboard,
  Network,
  Zap,
  Map,
  MessageSquarePlus,
  Columns,
  GanttChart,
  Lock,
  Plus,
  Pencil,
  Trash2,
  Clock,
} from 'lucide-react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { cn } from '../utils';
import { useStore } from '../store/useStore';
import { useAuthStore } from '../store/useAuthStore';
import { ROUTES } from '../routes';
import { useAICredits } from '../hooks/useAICredits';
import { useUserTier, useTrialStatus } from '../hooks/useUserTier';
import { canAccessView, getTierLimits } from '../lib/capabilities';

export function Sidebar() {
  const { t } = useTranslation();
  const inboxItems = useStore((state) => state.inboxItems);
  const { used, limit, remaining, loading } = useAICredits();
  const tier = useUserTier();
  const { isTrialActive, daysRemaining, hasNeverHadTrial } = useTrialStatus();
  const isUnlimited = limit === Infinity;
  const pct = !isUnlimited && limit > 0 ? Math.min(Math.round((used / limit) * 100), 100) : 0;
  const isLow = !isUnlimited && remaining <= Math.ceil(limit * 0.15);

  const session = useAuthStore((s) => s.session);
  const navigate = useNavigate();
  const location = useLocation();
  const isOnBrainPage = location.pathname === '/brain' || location.pathname.startsWith('/brain/');
  const { brains, activeBrainId, switchBrain, createBrain, renameBrain, deleteBrain } = useStore(
    useShallow((s) => ({
      brains: s.brains,
      activeBrainId: s.activeBrainId,
      switchBrain: s.switchBrain,
      createBrain: s.createBrain,
      renameBrain: s.renameBrain,
      deleteBrain: s.deleteBrain,
    }))
  );
  const limits = getTierLimits(tier);
  const canMultiBrain = limits.maxBrains > 1 || limits.maxBrains === -1;

  const [renamingBrainId, setRenamingBrainId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingBrainId, setDeletingBrainId] = useState<string | null>(null);
  const [creatingBrain, setCreatingBrain] = useState(false);
  const [newBrainName, setNewBrainName] = useState('');

  const navGroups = [
    {
      title: t('sidebar.app_title'),
      items: [
        {
          icon: LayoutDashboard,
          label: t('sidebar.dashboard'),
          to: ROUTES.dashboard,
          highlight: false,
        },
        {
          icon: Zap,
          label: t('sidebar.synapse'),
          to: ROUTES.synapse,
          badge: inboxItems.length,
          highlight: true,
          dataOnboarding: 'sidebar-synapse',
        },
        {
          icon: Network,
          label: t('sidebar.brains'),
          to: ROUTES.brain,
          highlight: true,
          dataOnboarding: 'sidebar-brain',
        },
      ],
    },
    {
      title: t('sidebar.group_act'),
      dataOnboarding: 'sidebar-views',
      items: [
        { icon: CheckCircle2, label: t('sidebar.tasks'), to: ROUTES.tasks },
        { icon: Columns, label: t('sidebar.kanban'), to: ROUTES.kanban, gateKey: 'kanban' },
        { icon: GanttChart, label: t('sidebar.gantt'), to: ROUTES.gantt, gateKey: 'gantt' },
        { icon: Calendar, label: t('sidebar.calendar'), to: ROUTES.calendar },
        { icon: CalendarDays, label: t('sidebar.events'), to: ROUTES.events },
        { icon: BookOpen, label: t('sidebar.notes'), to: ROUTES.notes },
      ],
    },
    {
      title: t('sidebar.group_think'),
      items: [
        { icon: Target, label: t('sidebar.goals'), to: ROUTES.goals },
        { icon: Users, label: t('sidebar.contacts'), to: ROUTES.contacts },
        { icon: Activity, label: t('sidebar.habits'), to: ROUTES.habits },
        { icon: Clock, label: t('sidebar.timeline'), to: ROUTES.timeline },
      ],
    },
    {
      title: t('sidebar.group_evaluate'),
      items: [
        { icon: Layers, label: t('sidebar.domains'), to: ROUTES.domains },
        { icon: PieChart, label: t('sidebar.month'), to: ROUTES.months },
        { icon: Wallet, label: t('sidebar.finances'), to: ROUTES.finances, gateKey: 'finances' },
      ],
    },
  ];

  const renderBrainItems = () => {
    const userId = session?.user?.id;
    return (
      <div className="ml-7 mt-0.5 space-y-0.5">
        {brains.map((brain) => (
          <div key={brain.id} className="group flex items-center justify-between">
            {renamingBrainId === brain.id ? (
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && renameValue.trim()) {
                    renameBrain(brain.id, renameValue.trim());
                    setRenamingBrainId(null);
                  }
                  if (e.key === 'Escape') setRenamingBrainId(null);
                }}
                onBlur={() => {
                  if (renameValue.trim()) renameBrain(brain.id, renameValue.trim());
                  setRenamingBrainId(null);
                }}
                autoFocus
                className="flex-1 bg-zinc-900 border border-violet-500/40 rounded-md px-2 py-1.5 text-sm text-zinc-100 focus:outline-none"
              />
            ) : deletingBrainId === brain.id ? (
              <div className="flex-1 flex items-center justify-between px-2 py-1.5">
                <span className="text-xs text-red-400">{t('sidebar.delete_confirm')}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      await deleteBrain(brain.id);
                      setDeletingBrainId(null);
                      if (brain.id === activeBrainId) {
                        const def = brains.find((b) => b.isDefault && b.id !== brain.id);
                        if (def && userId) {
                          await switchBrain(userId, def.id);
                          navigate('/brain');
                        }
                      }
                    }}
                    className="text-xs text-red-400 hover:text-red-300 font-medium"
                  >
                    {t('common.yes')}
                  </button>
                  <button
                    onClick={() => setDeletingBrainId(null)}
                    className="text-xs text-zinc-500 hover:text-zinc-300"
                  >
                    {t('common.no')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={async () => {
                    if (brain.id !== activeBrainId && userId) {
                      await switchBrain(userId, brain.id);
                    }
                    navigate('/brain');
                  }}
                  className={cn(
                    'flex-1 flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors text-left',
                    brain.id === activeBrainId && isOnBrainPage
                      ? 'text-violet-300 bg-violet-500/10'
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
                  )}
                >
                  <span
                    className={cn(
                      'w-1.5 h-1.5 rounded-full shrink-0',
                      brain.id === activeBrainId && isOnBrainPage ? 'bg-violet-500' : 'bg-zinc-600'
                    )}
                  />
                  <span className="truncate">{brain.title}</span>
                </button>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity pr-1">
                  <button
                    onClick={() => {
                      setRenamingBrainId(brain.id);
                      setRenameValue(brain.title);
                    }}
                    className="p-1 text-zinc-600 hover:text-zinc-300 transition-colors rounded"
                    title={t('common.rename')}
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  {!brain.isDefault && (
                    <button
                      onClick={() => setDeletingBrainId(brain.id)}
                      className="p-1 text-zinc-600 hover:text-red-400 transition-colors rounded"
                      title={t('common.delete')}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}

        {canMultiBrain ? (
          <>
            {limits.maxBrains === -1 || brains.length < limits.maxBrains ? (
              creatingBrain ? (
                <input
                  type="text"
                  value={newBrainName}
                  onChange={(e) => setNewBrainName(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && newBrainName.trim() && userId) {
                      const brain = await createBrain(userId, newBrainName.trim());
                      if (brain) {
                        await switchBrain(userId, brain.id);
                        navigate('/brain');
                      }
                      setNewBrainName('');
                      setCreatingBrain(false);
                    }
                    if (e.key === 'Escape') {
                      setNewBrainName('');
                      setCreatingBrain(false);
                    }
                  }}
                  onBlur={() => {
                    setNewBrainName('');
                    setCreatingBrain(false);
                  }}
                  autoFocus
                  placeholder={t('sidebar.brain_name_placeholder')}
                  className="w-full bg-zinc-900 border border-violet-500/40 rounded-md px-2 py-1.5 text-xs text-zinc-100 focus:outline-none placeholder:text-zinc-600"
                />
              ) : (
                <button
                  onClick={() => setCreatingBrain(true)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-zinc-500 hover:text-violet-400 transition-colors w-full"
                >
                  <Plus className="w-3 h-3" /> {t('sidebar.new_brain')}
                </button>
              )
            ) : (
              <a
                href="/abonnement"
                className="flex items-center justify-center mt-1 px-3 py-1.5 rounded-lg border border-violet-500/30 text-[11px] font-medium text-violet-400 hover:bg-violet-500/10 hover:border-violet-500/50 transition-colors"
              >
                {t('sidebar.upgrade_to_max')}
              </a>
            )}
          </>
        ) : (
          <a
            href="/abonnement"
            className="flex items-center justify-center mt-1 px-3 py-1.5 rounded-lg border border-violet-500/30 text-[11px] font-medium text-violet-400 hover:bg-violet-500/10 hover:border-violet-500/50 transition-colors"
          >
            {t('sidebar.available_with_pro')}
          </a>
        )}
      </div>
    );
  };

  const renderGroup = (group: (typeof navGroups)[number]) => (
    <div
      key={group.title}
      {...('dataOnboarding' in group && group.dataOnboarding
        ? { 'data-onboarding': group.dataOnboarding }
        : {})}
    >
      <h2 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 px-2">
        {group.title}
      </h2>
      <nav className="space-y-1">
        {group.items.map((item) => (
          <div key={item.label} className="contents">
            <NavLink
              to={item.to}
              {...('dataOnboarding' in item && item.dataOnboarding
                ? { 'data-onboarding': item.dataOnboarding }
                : {})}
              className={({ isActive }) =>
                cn(
                  'w-full flex items-center justify-between px-2 py-2 rounded-md text-sm transition-colors',
                  isActive
                    ? 'bg-zinc-800 text-white shadow-[inset_3px_0_0_#7c3aed]'
                    : item.highlight
                      ? 'text-zinc-300 hover:text-violet-200 hover:bg-violet-500/10 hover:shadow-[inset_3px_0_0_rgba(124,58,237,0.4)]'
                      : 'text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800'
                )
              }
            >
              <div className="flex items-center gap-3">
                <item.icon className="w-4 h-4" />
                {item.label}
                {'gateKey' in item && item.gateKey && !canAccessView(item.gateKey, tier) && (
                  <Lock className="w-3.5 h-3.5 text-zinc-400 opacity-70 ml-1" />
                )}
              </div>
              {'badge' in item && item.badge !== undefined && item.badge > 0 && (
                <span className="bg-blue-500/20 text-blue-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
            </NavLink>
            {item.label === 'Brains' && renderBrainItems()}
          </div>
        ))}
      </nav>
    </div>
  );

  return (
    <aside className="w-64 h-screen border-r border-zinc-700 bg-zinc-950 flex flex-col hidden md:flex shrink-0">
      <div className="p-6 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-zinc-100 text-zinc-950 flex items-center justify-center font-bold text-lg">
            V
          </div>
          <div>
            <h1 className="font-medium text-sm text-zinc-100">{t('sidebar.app_title')}</h1>
            <p className="text-xs text-zinc-500">{t('sidebar.personal_space')}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pt-6 space-y-8 pb-8">
        {/* Vues Principales */}
        {renderGroup(navGroups[0])}

        {/* Agis, Réfléchis, Évalue */}
        <div className="space-y-8">{navGroups.slice(1).map(renderGroup)}</div>
      </div>

      {/* Trial indicator */}
      {isTrialActive && (
        <div className="mx-3 mb-2 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
          <p className="text-[11px] font-medium text-violet-400 text-center">
            {t('sidebar.trial_days_remaining', { days: daysRemaining })}
          </p>
        </div>
      )}

      {/* AI Credits indicator */}
      <div className="px-3 py-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider">
            {t('sidebar.ai_credits')}
          </span>
          <span className={`text-[11px] font-medium ${isLow ? 'text-amber-400' : 'text-zinc-400'}`}>
            {loading
              ? '...'
              : isUnlimited
                ? t('sidebar.credits_unlimited')
                : t('sidebar.credits_remaining', { remaining })}
          </span>
        </div>
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${
              isLow ? 'bg-amber-500' : 'bg-violet-500'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-[10px] text-zinc-600 mt-1">
          {loading
            ? ''
            : isUnlimited
              ? t('sidebar.credits_unlimited_month')
              : t('sidebar.credits_usage', { used, limit })}
        </div>
      </div>

      {/* Activate trial link */}
      {hasNeverHadTrial && (
        <div className="mx-3 mb-2">
          <Link
            to={ROUTES.subscription}
            className="block text-center text-[11px] font-medium text-violet-400 hover:text-violet-300 py-1.5 px-3 rounded-lg border border-violet-500/20 hover:border-violet-500/30 hover:bg-violet-500/5 transition-colors"
          >
            {t('sidebar.activate_trial')}
          </Link>
        </div>
      )}

      <div className="p-4 border-t border-zinc-800">
        <nav className="space-y-1">
          <NavLink
            to={ROUTES.roadmap}
            className={({ isActive }) =>
              cn(
                'w-full flex items-center gap-3 px-2 py-2 rounded-md text-sm transition-colors',
                isActive
                  ? 'bg-zinc-800 text-white shadow-[inset_3px_0_0_#7c3aed]'
                  : 'text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800'
              )
            }
          >
            <Map className="w-4 h-4" />
            {t('sidebar.roadmap')}
          </NavLink>
          <NavLink
            to={ROUTES.feedback}
            className={({ isActive }) =>
              cn(
                'w-full flex items-center gap-3 px-2 py-2 rounded-md text-sm transition-colors',
                isActive
                  ? 'bg-zinc-800 text-white shadow-[inset_3px_0_0_#7c3aed]'
                  : 'text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800'
              )
            }
          >
            <MessageSquarePlus className="w-4 h-4" />
            {t('sidebar.feedback')}
          </NavLink>
        </nav>
      </div>

      {/* Legal links */}
      <div className="px-4 pb-4 pt-2">
        <p className="text-[10px] text-zinc-600 leading-relaxed text-center">
          <Link to={ROUTES.mentionsLegales} className="hover:text-zinc-300 transition-colors">
            {t('sidebar.legal_mentions')}
          </Link>
          {' · '}
          <Link to={ROUTES.cgu} className="hover:text-zinc-300 transition-colors">
            {t('sidebar.cgu')}
          </Link>
          {' · '}
          <Link to={ROUTES.cgv} className="hover:text-zinc-300 transition-colors">
            {t('sidebar.cgv')}
          </Link>
          {' · '}
          <Link to={ROUTES.confidentialite} className="hover:text-zinc-300 transition-colors">
            {t('sidebar.privacy')}
          </Link>
        </p>
      </div>
    </aside>
  );
}
